/**
 * Portal navigation organised by tenant workflow.
 * Hidden/internal items are kept in code for later subscription phases, but are
 * not exposed to beta tenants or reachable through the normal portal guard.
 */

import {
  LayoutDashboard,
  Users,
  GraduationCap,
  History,
  Settings,
  UserPlus,
  Building2,
  Shield,
  Siren,
  Heart,
  Eye,
  UserCheck,
  CalendarClock,
  UserCog,
  MessageSquare,
  Landmark,
  Briefcase,
  MapPin,
  Receipt,
  Banknote,
  FileText,
  PieChart,
  Archive,
  Crown,
  CreditCard,
  Lock,
} from 'lucide-react';

export const LEGACY_WORK_AREA_MAP = {
  '': 'organisation_wide',
  accounts: 'finance_payroll',
  staffing: 'operations_staffing',
  operations: 'operations_staffing',
  recruitment: 'recruitment_onboarding',
  compliance: 'compliance_quality',
  hr: 'hr_people',
  enquiries: 'support_enquiries',
};

export function normalizePrimaryWorkArea(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return LEGACY_WORK_AREA_MAP[normalized] || normalized || 'organisation_wide';
}

/** Primary work areas assignable on staff access accounts */
export const USER_DEPARTMENTS = [
  { id: 'organisation_wide', label: 'Organisation-wide', description: 'Prioritise the full workspace menu allowed by role' },
  { id: 'management_governance', label: 'Management & Governance', description: 'Prioritise oversight, settings, and governance work' },
  { id: 'operations_staffing', label: 'Operations & Staffing', description: 'Prioritise shifts, staffing requests, and day-to-day operations' },
  { id: 'recruitment_onboarding', label: 'Recruitment & Onboarding', description: 'Prioritise applicant pipeline and onboarding work' },
  { id: 'compliance_quality', label: 'Compliance & Quality', description: 'Prioritise evidence, training, audits, and quality checks' },
  { id: 'hr_people', label: 'HR & People', description: 'Prioritise people records, HR, recruitment, and training' },
  { id: 'finance_payroll', label: 'Finance & Payroll', description: 'Prioritise timesheets, payroll, invoices, and accounts' },
  { id: 'clients_service_users', label: 'Clients & Service Users', description: 'Prioritise client organisations, service users, and care locations' },
  { id: 'support_enquiries', label: 'Support & Enquiries', description: 'Prioritise support conversations and incoming enquiries' },
];

/** Primary work area -> preferred section ordering. This is presentation only. */
export const WORK_AREA_SECTION_PRIORITY = {
  organisation_wide: [],
  management_governance: ['admin', 'overview', 'compliance', 'finance'],
  operations_staffing: ['care_delivery', 'overview', 'people', 'enquiries'],
  recruitment_onboarding: ['people', 'overview', 'compliance'],
  compliance_quality: ['compliance', 'admin', 'overview'],
  hr_people: ['people', 'compliance', 'overview'],
  finance_payroll: ['finance', 'overview', 'admin'],
  clients_service_users: ['care_delivery', 'enquiries', 'overview'],
  support_enquiries: ['enquiries', 'overview', 'care_delivery'],
};

const navItem = (def) => def;

export const PORTAL_NAV_SECTIONS = [
  {
    id: 'overview',
    label: 'Overview',
    description: 'Daily control tower',
    items: [
      navItem({
        name: 'Dashboard',
        href: '/portal/dashboard',
        icon: LayoutDashboard,
        tourKey: 'dashboard',
        section: 'overview',
      }),
    ],
  },
  {
    id: 'people',
    label: 'People',
    description: 'Recruit and manage workers',
    items: [
      navItem({
        name: 'Recruitment',
        href: '/portal/recruitment',
        icon: UserCheck,
        tourKey: 'recruitment',
        section: 'people',
      }),
      navItem({
        name: 'Employees',
        href: '/portal/employees',
        icon: Users,
        tourKey: 'employees',
        section: 'people',
        // Employees remains a first-class guarded route, but People workspace
        // sub-navigation owns stage switching. Hide this duplicate child from
        // the global module menu so the sidebar has one clear People entry.
        moduleHidden: true,
      }),
      navItem({
        name: 'Staff communications',
        href: '/portal/staff-communications',
        icon: MessageSquare,
        tourKey: 'staff-communications',
        section: 'people',
        managerOnly: true,
        // The People workspace already exposes this as a dedicated sub-view.
        // Keep the global module compact while still registering the route with
        // the central direct-link guard and active-module resolver.
        moduleHidden: true,
      }),
    ],
  },
  {
    id: 'care_delivery',
    label: 'Care Delivery',
    description: 'Clients, rotas, and care locations',
    items: [
      navItem({
        name: 'Client Organisations',
        href: '/portal/organisations',
        icon: Landmark,
        tourKey: 'organisations',
        section: 'care_delivery',
      }),
      navItem({
        name: 'Staffing Requests',
        href: '/portal/staffing-requests',
        icon: Briefcase,
        tourKey: 'staffing-requests',
        section: 'care_delivery',
        featureKey: 'shift_assignment_safety',
      }),
      navItem({
        name: 'Shifts',
        href: '/portal/shifts',
        icon: CalendarClock,
        tourKey: 'shifts',
        section: 'care_delivery',
        featureKey: 'shift_assignment_safety',
      }),
      navItem({
        name: 'Service Users',
        href: '/portal/service-users',
        icon: Heart,
        tourKey: 'service-users',
        section: 'care_delivery',
        featureKey: 'service_user_readiness_basics',
      }),
      navItem({
        name: 'Care Locations',
        href: '/portal/care-locations',
        icon: MapPin,
        tourKey: 'care-locations',
        section: 'care_delivery',
      }),
    ],
  },
  {
    id: 'enquiries',
    label: 'Enquiries',
    description: 'Inbound care interest',
    items: [
      navItem({
        name: 'Care Enquiries',
        href: '/portal/care-enquiries',
        icon: MessageSquare,
        tourKey: 'care-enquiries',
        section: 'enquiries',
        internalOnly: true,
      }),
      navItem({
        name: 'Portal Inbox',
        href: '/portal/inbox',
        icon: MessageSquare,
        tourKey: 'portal-inbox',
        section: 'enquiries',
        managerOnly: true,
      }),
      navItem({
        name: 'Sales Leads',
        href: '/portal/sales-leads',
        icon: UserPlus,
        tourKey: 'sales-leads',
        section: 'enquiries',
        managerOnly: true,
      }),
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    description: 'Pay workers and bill clients',
    items: [
      navItem({
        name: 'Timesheets',
        href: '/portal/timesheets',
        icon: Receipt,
        tourKey: 'timesheets',
        section: 'finance',
        featureKey: 'attendance_timesheet_readiness',
      }),
      navItem({
        name: 'Payroll',
        href: '/portal/payroll',
        icon: Banknote,
        tourKey: 'payroll',
        section: 'finance',
        adminOnly: true,
        featureKey: 'payroll_and_invoicing',
      }),
      navItem({
        name: 'Invoices',
        href: '/portal/invoices',
        icon: FileText,
        tourKey: 'invoices',
        section: 'finance',
        adminOnly: true,
        featureKey: 'payroll_and_invoicing',
      }),
      navItem({
        name: 'Finance Overview',
        href: '/portal/accounts',
        icon: PieChart,
        tourKey: 'accounts',
        section: 'finance',
        adminOnly: true,
        featureKey: 'payroll_and_invoicing',
      }),
    ],
  },
  {
    id: 'compliance',
    label: 'Compliance',
    description: 'CQC evidence and safer recruitment',
    items: [
      navItem({
        name: 'Compliance Hub',
        href: '/portal/compliance-centre',
        icon: Building2,
        tourKey: 'compliance-hub',
        section: 'compliance',
      }),
      navItem({
        name: 'Compliance Command',
        href: '/portal/compliance-alerts',
        icon: Siren,
        tourKey: 'compliance-command',
        section: 'compliance',
        managerOnly: true,
      }),
      navItem({
        name: 'Privacy & DSAR',
        href: '/portal/privacy-requests',
        icon: Lock,
        tourKey: 'privacy-requests',
        section: 'compliance',
        adminOnly: true,
      }),
      navItem({
        name: 'Feedback',
        href: '/portal/feedback',
        icon: MessageSquare,
        tourKey: 'feedback',
        section: 'compliance',
      }),
      navItem({
        name: 'Training',
        href: '/portal/training',
        icon: GraduationCap,
        tourKey: 'training',
        section: 'compliance',
      }),
      navItem({
        name: 'DBS Register',
        href: '/portal/dbs-register',
        icon: Shield,
        tourKey: 'dbs-register',
        section: 'compliance',
      }),
      navItem({
        name: 'Form Templates',
        href: '/portal/templates',
        icon: FileText,
        tourKey: 'templates',
        section: 'compliance',
        internalOnly: true,
      }),
      navItem({
        name: 'Template Library',
        href: '/portal/template-library',
        icon: Archive,
        tourKey: 'template-library',
        section: 'compliance',
        adminOnly: true,
        featureKey: 'branded_pdfs_documents',
        internalOnly: true,
      }),
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    description: 'Access control and configuration',
    items: [
      navItem({
        name: 'Audit & Inspection',
        href: '/portal/audit',
        icon: History,
        tourKey: 'audit',
        section: 'admin',
      }),
      navItem({
        name: 'Team & Permissions',
        href: '/portal/admin-users',
        icon: UserCog,
        tourKey: 'admin-users',
        section: 'admin',
        adminOnly: true,
      }),
      navItem({
        name: 'Plans & Billing',
        href: '/portal/plans-billing',
        icon: CreditCard,
        tourKey: 'plans-billing',
        section: 'admin',
        workspaceOwnerOnly: true,
      }),
      navItem({
        name: 'CareOps Admin',
        href: '/portal/careops-admin',
        icon: Crown,
        tourKey: 'careops-admin',
        section: 'admin',
        ownerOnly: true,
      }),
      navItem({
        name: 'Integrations',
        href: '/portal/settings/integrations',
        icon: MessageSquare,
        tourKey: 'settings-integrations',
        section: 'admin',
        adminOnly: true,
      }),
      navItem({
        name: 'Organisation Settings',
        href: '/portal/settings',
        icon: Settings,
        tourKey: 'settings',
        section: 'admin',
      }),
    ],
  },
];

const AUDITOR_NAV_NAMES = new Set(['Dashboard', 'Audit & Inspection']);

const ROUTE_ALIASES = [
  { match: '/portal/people/communications', navPath: '/portal/staff-communications' },
  { match: '/portal/forms', navPath: '/portal/templates' },
  { match: '/portal/applications', navPath: '/portal/recruitment' },
  { match: '/portal/cqc-dashboard', navPath: '/portal/compliance-centre' },
  { match: '/portal/incidents', navPath: '/portal/compliance-centre' },
  { match: '/portal/global-audit', navPath: '/portal/audit' },
  { match: '/portal/sales-inbox', navPath: '/portal/inbox' },
  { match: '/portal/scheduled-requests', navPath: '/portal/compliance-centre' },
];

export const PORTAL_NAV_MODULES = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    childHrefs: ['/portal/dashboard'],
  },
  {
    id: 'people',
    label: 'People',
    icon: Users,
    childHrefs: ['/portal/recruitment', '/portal/employees', '/portal/staff-communications'],
  },
  {
    id: 'clients-care',
    label: 'Clients & Care',
    icon: Heart,
    childHrefs: ['/portal/organisations', '/portal/service-users', '/portal/care-locations'],
  },
  {
    id: 'staffing',
    label: 'Staffing',
    icon: Briefcase,
    childHrefs: ['/portal/staffing-requests', '/portal/shifts'],
  },
  {
    id: 'enquiries-sales',
    label: 'Enquiries & Sales',
    icon: MessageSquare,
    childHrefs: ['/portal/inbox', '/portal/sales-leads'],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: PieChart,
    childHrefs: ['/portal/accounts', '/portal/timesheets', '/portal/payroll', '/portal/invoices'],
  },
  {
    id: 'quality-compliance',
    label: 'Quality & Compliance',
    icon: Shield,
    childHrefs: ['/portal/compliance-centre', '/portal/compliance-alerts', '/portal/training', '/portal/dbs-register', '/portal/feedback'],
  },
  {
    id: 'audit-inspection',
    label: 'Audit & Inspection',
    icon: History,
    childHrefs: ['/portal/audit'],
  },
  {
    id: 'workspace',
    label: 'Workspace',
    icon: Settings,
    childHrefs: ['/portal/settings/integrations', '/portal/settings', '/portal/admin-users', '/portal/plans-billing'],
  },
];

const FEATURE_ACCESS_COPY = {
  attendance_timesheet_readiness: {
    label: 'Attendance and timesheets',
    upgradePlan: 'Essential',
  },
  shift_assignment_safety: {
    label: 'Staffing requests and shifts',
    upgradePlan: 'Essential',
  },
  service_user_readiness_basics: {
    label: 'Service user readiness',
    upgradePlan: 'Essential',
  },
  payroll_and_invoicing: {
    label: 'Payroll, invoices and accounts',
    upgradePlan: 'Professional',
  },
  branded_pdfs_documents: {
    label: 'Branded PDF document library',
    upgradePlan: 'Professional or add-on',
  },
};

const BACKEND_FEATURE_GATED_FEATURES = new Set([
  'attendance_timesheet_readiness',
  'shift_assignment_safety',
  'service_user_readiness_basics',
  'payroll_and_invoicing',
]);

function enforcementForItem(item, access) {
  const enforcement = ['Sidebar and direct-link guard'];
  if (item.internalOnly) {
    enforcement.push('Internal CareOps tool - hidden from tenants');
  }
  if (item.adminOnly) {
    enforcement.push('Tenant admin role required');
  }
  if (item.managerOnly) {
    enforcement.push('Manager or admin role required');
  }
  if (item.featureKey) {
    enforcement.push(
      BACKEND_FEATURE_GATED_FEATURES.has(item.featureKey)
        ? 'Backend subscription gate'
        : 'Subscription visibility gate',
    );
  }
  if (access?.reason && !access.available) {
    enforcement.push(access.reason);
  }
  return [...new Set(enforcement)];
}

function featureLabel(featureKey) {
  return FEATURE_ACCESS_COPY[featureKey]?.label || featureKey?.replace(/_/g, ' ') || 'Feature';
}

function featureUpgradePlan(featureKey) {
  return FEATURE_ACCESS_COPY[featureKey]?.upgradePlan || 'Upgrade';
}

export function resolvePortalItemAccess(item, user, { isAdmin = false, isManagerOrAdmin = false, isAuditor = false } = {}) {
  const role = user?.role;
  const planFeatures = user?.workspace_entitlements?.features || null;

  if (isAuditor) {
    return AUDITOR_NAV_NAMES.has(item.name)
      ? { visible: true, available: true, reason: null }
      : { visible: false, available: false, reason: 'Auditor access is limited to inspection areas.' };
  }

  if (role === 'platform_owner') {
    return item.ownerOnly
      ? { visible: true, available: true, reason: null }
      : { visible: false, available: false, reason: 'Platform owners use the CareOps owner console, not tenant workspace pages.' };
  }

  if (item.ownerOnly) {
    return { visible: false, available: false, reason: 'CareOps owner only.' };
  }

  if (item.workspaceOwnerOnly && role !== 'super_admin') {
    return { visible: false, available: false, reason: 'Workspace Owner permission required.' };
  }

  if (item.internalOnly) {
    return { visible: false, available: false, reason: 'Internal CareOps setup tool.' };
  }

  if (item.featureKey && planFeatures && planFeatures[item.featureKey] !== true) {
    return {
      visible: false,
      available: false,
      reason: `${featureLabel(item.featureKey)} requires ${featureUpgradePlan(item.featureKey)}.`,
      upgradePlan: featureUpgradePlan(item.featureKey),
      featureKey: item.featureKey,
    };
  }

  if (item.adminOnly && !isAdmin) {
    return { visible: false, available: false, reason: 'Tenant admin permission required.' };
  }

  if (item.managerOnly && !isManagerOrAdmin) {
    return { visible: false, available: false, reason: 'Manager or admin permission required.' };
  }

  return { visible: true, available: true, reason: null };
}

function orderSectionsByWorkArea(sections, user) {
  const workArea = normalizePrimaryWorkArea(user?.department);
  const preferred = WORK_AREA_SECTION_PRIORITY[workArea] || [];
  if (!preferred.length) return sections;

  const originalIndex = new Map(sections.map((section, index) => [section.id, index]));
  const preferredIndex = new Map(preferred.map((sectionId, index) => [sectionId, index]));

  return [...sections].sort((a, b) => {
    const aPreferred = preferredIndex.has(a.id);
    const bPreferred = preferredIndex.has(b.id);
    if (aPreferred && bPreferred) return preferredIndex.get(a.id) - preferredIndex.get(b.id);
    if (aPreferred) return -1;
    if (bPreferred) return 1;
    return originalIndex.get(a.id) - originalIndex.get(b.id);
  });
}

/**
 * Filter navigation sections for the current user.
 */
export function filterPortalNavigation(user, authHelpers = {}) {
  const authorizedSections = PORTAL_NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      const access = resolvePortalItemAccess(item, user, authHelpers);
      return access.visible && access.available;
    }),
  })).filter((section) => section.items.length > 0);

  return orderSectionsByWorkArea(authorizedSections, user);
}

/** Flat list of all nav items (for help / search) */
export function getAllPortalNavItems() {
  return PORTAL_NAV_SECTIONS.flatMap((section) =>
    section.items.map((item) => ({ ...item, sectionLabel: section.label })),
  );
}

function normalizePortalPath(pathname = '') {
  const pathWithoutQuery = String(pathname || '').split('?')[0].split('#')[0];
  const path = pathWithoutQuery.replace(/\/$/, '') || '/portal/dashboard';
  return ROUTE_ALIASES.find((alias) => (
    path === alias.match || path.startsWith(`${alias.match}/`)
  ))?.navPath || path;
}

export function getPortalNavigationModules(user, authHelpers = {}) {
  const allItemsByHref = new Map(getAllPortalNavItems().map((item) => [item.href.split('?')[0], item]));

  return PORTAL_NAV_MODULES.map((module) => {
    const items = module.childHrefs
      .map((href) => allItemsByHref.get(href))
      .filter(Boolean)
      .filter((item) => item.moduleHidden !== true)
      .filter((item) => {
        const access = resolvePortalItemAccess(item, user, authHelpers);
        return access.visible && access.available;
      });

    return {
      ...module,
      items,
      href: items.length === 1 ? items[0].href : null,
      singleItem: items.length === 1,
    };
  }).filter((module) => module.items.length > 0);
}

export function findActivePortalNavItem(pathname, search = '') {
  const path = normalizePortalPath(pathname);
  const activeTab = new URLSearchParams(String(search || '').replace(/^\?/, '')).get('tab');
  const items = getAllPortalNavItems();

  if (path === '/portal/compliance-centre' && activeTab === 'incidents') {
    return items.find((item) => item.href === '/portal/compliance-centre') || null;
  }

  return items.find((item) => {
    const baseHref = item.href.split('?')[0];
    return path === baseHref || path.startsWith(`${baseHref}/`);
  }) || null;
}

export function isPortalNavItemActive(item, pathname, search = '') {
  const activeItem = findActivePortalNavItem(pathname, search);
  return Boolean(activeItem && activeItem.href.split('?')[0] === item.href.split('?')[0]);
}

export function findActivePortalNavigationModule(pathname, search = '', modules = PORTAL_NAV_MODULES) {
  const activeItem = findActivePortalNavItem(pathname, search);
  if (!activeItem) return null;
  const activeHref = activeItem.href.split('?')[0];
  return modules.find((module) => module.childHrefs.includes(activeHref)) || null;
}

/** Resolve whether a path is reachable for the user (route guard helper) */
export function canAccessPortalPath(pathname, user, authHelpers) {
  const normalizedPath = normalizePortalPath(pathname);

  for (const section of PORTAL_NAV_SECTIONS) {
    for (const item of section.items) {
      const baseHref = item.href.split('?')[0];
      if (normalizedPath === baseHref || normalizedPath.startsWith(`${baseHref}/`)) {
        const access = resolvePortalItemAccess(item, user, authHelpers);
        return access.visible && access.available;
      }
    }
  }
  return false;
}

export function getPortalAccessSummary(user, authHelpers = {}) {
  const availableSections = [];
  const locked = [];
  const internal = [];

  PORTAL_NAV_SECTIONS.forEach((section) => {
    const availableItems = [];

    section.items.forEach((item) => {
      const access = resolvePortalItemAccess(item, user, authHelpers);
      const summaryItem = {
        name: item.name,
        href: item.href,
        section: section.label,
        reason: access.reason,
        upgradePlan: access.upgradePlan,
        featureKey: access.featureKey || item.featureKey,
        enforcement: enforcementForItem(item, access),
      };

      if (access.visible && access.available) {
        availableItems.push(summaryItem);
      } else if (item.internalOnly) {
        internal.push(summaryItem);
      } else if (access.upgradePlan || item.featureKey) {
        locked.push(summaryItem);
      }
    });

    if (availableItems.length) {
      availableSections.push({
        id: section.id,
        label: section.label,
        description: section.description,
        items: availableItems,
      });
    }
  });

  return {
    availableSections,
    locked,
    internal,
  };
}

/** Header quick actions by department */
export function getHeaderQuickActions(user, authHelpers) {
  const { isAdmin, isManagerOrAdmin, isAuditor } = authHelpers;
  if (isAuditor) return { showInspectionMode: false, showAddEmployee: false, searchPlaceholder: 'Search audit log' };

  const dept = normalizePrimaryWorkArea(user?.department);

  if (dept === 'finance_payroll' || (dept === 'organisation_wide' && isAdmin)) {
    return {
      showInspectionMode: isAdmin,
      showAddEmployee: false,
      searchPlaceholder: 'Search employees',
      searchHref: '/portal/employees',
      primaryAction: { label: 'Timesheets', href: '/portal/timesheets', icon: Receipt },
    };
  }

  if (dept === 'operations_staffing') {
    return {
      showInspectionMode: isManagerOrAdmin,
      showAddEmployee: false,
      searchPlaceholder: 'Search staffing requests',
      searchHref: '/portal/staffing-requests',
      primaryAction: { label: 'Staffing', href: '/portal/staffing-requests', icon: Briefcase },
    };
  }

  if (dept === 'recruitment_onboarding') {
    return {
      showInspectionMode: isManagerOrAdmin,
      showAddEmployee: false,
      searchPlaceholder: 'Search applicants',
      searchHref: '/portal/recruitment',
      primaryAction: { label: 'Recruitment', href: '/portal/recruitment', icon: UserCheck },
    };
  }

  return {
    showInspectionMode: isAdmin,
    showAddEmployee: false,
    searchPlaceholder: 'Search employees',
    searchHref: '/portal/employees',
    primaryAction: isManagerOrAdmin
      ? { label: 'Invite Applicant', href: '/portal/recruitment', icon: UserPlus }
      : null,
  };
}
